import EastIcon from "@mui/icons-material/East";
import React, { useState, useEffect } from "react";
import OrderImg from "../../../../assets/images/order-banner-image.png";
import PropTypes from "prop-types";
import config from "config/config";

const TimeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M11.9994 9.5V13.5L14.4994 15M11.9994 5C7.30497 5 3.49939 8.80558 3.49939 13.5C3.49939 18.1944 7.30497 22 11.9994 22C16.6938 22 20.4994 18.1944 20.4994 13.5C20.4994 8.80558 16.6938 5 11.9994 5ZM11.9994 5V2M9.99939 2H13.9994M20.3284 5.59204L18.8284 4.09204L19.5784 4.84204M3.67041 5.59204L5.17041 4.09204L4.42041 4.84204"
      stroke="#FFCC00"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const OrderNowBanner = ({ campaign, restaurantDetails, expiryDate }) => {
  // Function to calculate time remaining
  const calculateTimeLeft = () => {
    // If no expiry date is provided, treat as expired (don't show banner)
    if (!expiryDate) return { days: 0, hours: 0, minutes: 0, hasExpired: true };

    const expiryDateTime = new Date(expiryDate);
    const currentDateTime = new Date();

    // Calculate the difference in milliseconds
    const timeDifference = expiryDateTime - currentDateTime;

    // Check if the offer has expired
    if (timeDifference <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        hasExpired: true,
      };
    }

    // Calculate days, hours, minutes
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

    return {
      days,
      hours,
      minutes,
      hasExpired: false,
    };
  };

  // Initialize state with calculated value to prevent flash
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft());

  useEffect(() => {
    // Initial calculation (redundant now but keeping for consistency)
    setTimeLeft(calculateTimeLeft());

    // Set up interval to update every minute
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 60000); // Update every minute

    // Clean up the interval on unmount
    return () => clearInterval(timer);
  }, [expiryDate]);

  // Return null (hide the banner) if the offer has expired
  if (timeLeft?.hasExpired) {
    return null;
  }

  // Check if the order now URL exists
  const hasOrderNowUrl =
    restaurantDetails?.orderNowUrl && restaurantDetails.orderNowUrl.trim() !== "";

  return (
    <div className="w-full bg-[#238856] text-white rounded-[16px] overflow-hidden mb-11 -mt-4">
      <div className="container mx-auto p-[15px] lg:py-0 lg:px-6 lg:pt-2 flex flex-col md:flex-row justify-between">
        {/* Left content */}
        <div
          style={{ marginTop: !hasOrderNowUrl && 22 }}
          className="flex-1 mt-1 text-center md:text-left mb-4 md:mb-0"
        >
          {campaign?.type === "FIXED" ? (
            <>
              <h2
                style={{ marginTop: campaign.minimumOrderValueInCents == "0" && 15 }}
                className="text-base text-left md:text-lg lg:text-[21px] lg:leading-[135%] font-semibold"
              >
                Enjoy Additional Reward Of ${+campaign?.fixedCashback / 100}(
                {`${campaign?.fixedCashback} points`}) On Your First Order From{" "}
                <span
                  onClick={() => {
                    window.open(
                      `${
                        config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"]
                          .API_LANDING_PAGE_BASE_URL
                      }/restaurants/${restaurantDetails?.resaturantPrefix}`,
                      "_blank"
                    );
                  }}
                  className="hover:underline cursor-pointer"
                >
                  {restaurantDetails?.restaurantName}
                </span>{" "}
              </h2>

              {campaign.minimumOrderValueInCents !== "0" && (
                <p className="text-base md:text-base text-white mt-2 text-left">
                  Order minimum value must be greater than $
                  {campaign.minimumOrderValueInCents / 100}
                </p>
              )}
            </>
          ) : (
            <>
              <h2
                style={{ marginTop: campaign.minimumOrderValueInCents == "0" && 15 }}
                className="text-base text-left md:text-lg lg:text-[21px] lg:leading-[135%] font-semibold"
              >
                Enjoy An Additional Cashback Of +{campaign.multiplierCashback}X On Top Of Your
                Existing Cashback Incentive -{" "}
                <span
                  className="hover:underline cursor-pointer"
                  onClick={() => {
                    window.open(
                      `${
                        config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"]
                          .API_LANDING_PAGE_BASE_URL
                      }/restaurants/${restaurantDetails?.resaturantPrefix}`,
                      "_blank"
                    );
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {restaurantDetails?.restaurantName}
                </span>
                .
              </h2>

              {campaign.minimumOrderValueInCents !== "0" && (
                <p className="text-base md:text-base text-left text-white mt-2">
                  Get bonus rewards points with every order{" "}
                  {campaign.minimumOrderValueInCents === "0" && "."}
                  {campaign.minimumOrderValueInCents !== "0" &&
                    `from $${campaign.minimumOrderValueInCents / 100}`}
                </p>
              )}
            </>
          )}

          {hasOrderNowUrl && (
            <button
              onClick={() => {
                window.open(restaurantDetails.orderNowUrl, "_blank");
              }}
              className="items-center bg-white text-emerald-800 px-4 py-2 mb-1 rounded-md font-medium text-sm hover:bg-emerald-50 transition-colors mt-3 hidden md:inline-flex"
            >
              Order now <EastIcon className="ml-2 h-4 w-4 " />
            </button>
          )}
        </div>

        {/* Right content with image and countdown */}
        <div className="flex gap-10 flex-row items-center justify-between md:justify-start">
          {/* Person image */}
          <div className="hidden lg:block w-32 md:w-[208px] h-[150px] mb-4 md:mb-0">
            <img src={OrderImg} alt="Person holding food items" className="h-full w-full" />
          </div>

          {hasOrderNowUrl && (
            <button
              onClick={() => {
                window.open(restaurantDetails.orderNowUrl, "_blank");
              }}
              className="items-center bg-white text-emerald-800 px-4 py-2 rounded-md font-medium text-sm hover:bg-emerald-50 transition-colors mt-3 md:hidden inline-flex"
            >
              Order now <EastIcon className="ml-2 h-4 w-4" />
            </button>
          )}

          {/* Countdown timer */}
          {expiryDate && (
            <div className="text-center">
              <p className="text-base uppercase md:-mt-2 md:mb-2 text-white md:block hidden">
                OFFER EXPIRES IN
              </p>
              <div className="flex items-center mt-1 md:mt-0 md:items-start md:space-x-2">
                <div className="md:hidden">
                  <TimeIcon />
                </div>
                {/* Days */}
                <div className="md:bg-white md:text-black text-center rounded p-2 md:w-14 flex flex-col items-center">
                  <span className="md:text-2xl text-base font-bold md:font-normal">
                    {timeLeft?.days}
                  </span>
                  <span className="text-[9px] md:text-xs uppercase ">days</span>
                </div>

                {/* Hours */}
                <div className="md:bg-white md:text-black text-center rounded p-2 md:w-14 flex flex-col items-center">
                  <span className="md:text-2xl text-base font-bold md:font-normal">
                    {timeLeft?.hours}
                  </span>
                  <span className="text-[9px] md:text-xs uppercase ">hrs</span>
                </div>

                {/* Minutes */}
                <div className="md:bg-white md:text-black text-center rounded p-2 md:w-14 flex flex-col items-center">
                  <span className="md:text-2xl text-base font-bold md:font-normal">
                    {timeLeft?.minutes}
                  </span>
                  <span className="text-[9px] md:text-xs uppercase ">mins</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderNowBanner;

OrderNowBanner.propTypes = {
  campaign: PropTypes.object,
  restaurantDetails: PropTypes.any,
  expiryDate: PropTypes.string,
};
